/**
 * 文件简介：
 * - 睡眠大屏患者视图共用的 ECharts 图表逻辑（图表性能与内存优化）
 * - 按 roomId 复用 ECharts 实例；init 单入口；定时更新仅 setOption series
 * - 1.5 阶段：真实波线只由 realtime 写入 waveData；离屏/已结束患者 prune + dispose
 * - 第二阶段：chartPool 挂在 sleep.vue，轮播/Welcome↔Default 复用实例
 * - 第三阶段：固定 8 槽元素池（patientSlotPool），图表按 {view}-slot-N 绑定 DOM
 * - 第四阶段：可见槽位且 DOM 有宽高才 init；updateChart 禁止 fallback init；全屏 __screenLifecycleStats
 * - 生命周期计数：window.__chartLifecycleStats.summary() 查看 init/dispose/setOption 次数
 * 使用方法：混入 SleepViewDefault / SleepViewWelcome（需 sleep.vue provide chartPool + keep-alive）
 */
import * as echarts from 'echarts';
import config from '@/config';
import logger from '@/utils/logger';
import chartLifecycleStats from '@/utils/chartLifecycleStats';
import screenLifecycleStats from '@/utils/screenLifecycleStats';
import {
  createWavePoint,
  trimWaveBandInPlace,
  compressWaveBandInPlace,
  WAVE_BANDS
} from '@/utils/waveDataUtils';

/** 清空指定槽位 ECharts 实例（模块级，避免 mixin 合并边界问题） */
function clearSlotChartInstance(vm, slotIndex) {
  if (!vm || typeof vm.getChartSlotKey !== 'function') return;
  const slotKey = vm.getChartSlotKey(slotIndex);
  const pool = vm.getChartPool && vm.getChartPool();
  if (!pool) return;
  const chart = pool.instances[slotKey];
  const emptyOption = vm.getEmptyChartOption();
  if (chart && !(chart.isDisposed && chart.isDisposed())) {
    chart.setOption(emptyOption, { notMerge: true, lazyUpdate: false, silent: true });
    pool.optionCache[slotKey] = emptyOption;
  }
}

export default {
  inject: {
    chartPool: { default: null },
  },

  created() {
    // 单测或未注入 chartPool 时回退到本地池
    if (!this.chartPool) {
      this._localChartPool = { instances: {}, optionCache: {}, slotBinding: {} };
    }
  },

  /**
   * keep-alive 激活：DOM 重新挂载后绑定池中实例或 init，优先 reuse 而非全量 dispose
   */
  activated() {
    this.$nextTick(() => {
      if (typeof this.initCharts === 'function') {
        this.initCharts();
      }
    });
  },

  /**
   * keep-alive 停用：不 dispose 父级 chartPool（实例由 sleep.vue 统一管理）
   */
  deactivated() {
    logger.debug('[ChartLifecycle] SleepView deactivated，保留 chartPool 实例');
  },

  methods: {
    /** 父级实例池（sleep.vue provide）；无注入时使用 _localChartPool */
    getChartPool() {
      return this.chartPool || this._localChartPool;
    },

    /**
     * 根据 roomKey（如 room-46）在 appointmentData 中查找预约项
     */
    findAppointmentByRoomKey(roomKey) {
      const parent = this.$parent;
      if (!parent || !Array.isArray(parent.appointmentData)) return null;
      const roomMatch = /^room-(.+)$/.exec(roomKey || '');
      if (roomMatch) {
        const roomId = roomMatch[1];
        return parent.appointmentData.find(
          (item) => item && item.roomId != null && String(item.roomId) === String(roomId)
        );
      }
      const apptMatch = /^appt-(.+)$/.exec(roomKey || '');
      if (apptMatch) {
        const apptId = apptMatch[1];
        return parent.appointmentData.find(
          (item) => item && item.id != null && String(item.id) === String(apptId)
        );
      }
      return null;
    },
    /**
     * 槽位图表键：含视图前缀，避免 keep-alive 下 Welcome/Default 共用 slot-N 导致 DOM 错绑
     */
    getChartSlotKey(slotIndex) {
      const prefix = this.chartViewPrefix || 'default';
      return `${prefix}-slot-${slotIndex}`;
    },

    /**
     * 患者业务键（用于判断槽位是否换了患者，需全量 setOption）
     */
    getPatientBindingKey(item) {
      if (!item) return null;
      if (item.roomId != null && item.roomId !== '') {
        return `room-${item.roomId}`;
      }
      if (item.id != null && item.id !== '') {
        return `appt-${item.id}`;
      }
      return `mock-${item.patientName || 'unknown'}`;
    },

    /** @deprecated 第三阶段请使用 getChartSlotKey / getPatientBindingKey */
    getChartRoomKey(item, index) {
      if (item) return this.getPatientBindingKey(item) || `empty-${index}`;
      return `empty-${index}`;
    },

    /** 图表容器 DOM id（含 view 前缀，避免 keep-alive 双视图 id 冲突） */
    /** 图表容器是否已布局（避免 v-show 隐藏或 0 宽高时 echarts.init） */
    isChartDomReady(chartDom) {
      if (!chartDom) return false;
      return chartDom.clientWidth > 0 && chartDom.clientHeight > 0;
    },

    /**
     * DOM 尚未有宽高时延后 init（最多重试几次，避免 updateChart-fallback 在 0 尺寸容器上 init）
     */
    scheduleSlotChartInitWhenReady(slotIndex, item, attempt = 0) {
      const maxAttempts = 5;
      if (attempt >= maxAttempts) return;
      const delay = attempt === 0 ? 0 : 80;
      const run = () => {
        const chartDom = document.getElementById(this.getChartDomId(slotIndex));
        if (!this.isChartDomReady(chartDom)) {
          this.scheduleSlotChartInitWhenReady(slotIndex, item, attempt + 1);
          return;
        }
        this.initSlotChart(slotIndex, item, true);
      };
      if (delay === 0) {
        requestAnimationFrame(run);
      } else {
        const tid = setTimeout(run, delay);
        this.chartResizeTimers.push(tid);
      }
    },

    /**
     * 定时刷新路径：实例缺失时走 initSlotChart 单入口，禁止在此直接 echarts.init
     */
    ensureSlotChartInstance(slotIndex, item) {
      if (!item) return null;
      const slot = (this.patientSlotPool || []).find((s) => s.slotIndex === slotIndex);
      if (slot && !slot.visible) return null;

      const slotKey = this.getChartSlotKey(slotIndex);
      const pool = this.getChartPool();
      let chart = pool.instances[slotKey];
      if (chart && !(chart.isDisposed && chart.isDisposed())) {
        return chart;
      }

      const chartDom = document.getElementById(this.getChartDomId(slotIndex));
      if (!this.isChartDomReady(chartDom)) {
        return null;
      }

      const existingOnDom = echarts.getInstanceByDom(chartDom);
      if (existingOnDom) {
        chartLifecycleStats.recordBindExistingDom(slotKey, { domId: this.getChartDomId(slotIndex), from: 'ensureSlot' });
        pool.instances[slotKey] = existingOnDom;
        pool.slotBinding[slotKey] = this.getPatientBindingKey(item);
        return existingOnDom;
      }

      this.initSlotChart(slotIndex, item, true);
      return pool.instances[slotKey] || null;
    },

    /** 图表容器 DOM id（含 view 前缀，避免 keep-alive 双视图 id 冲突） */
    getChartDomId(slotIndex) {
      const prefix = this.chartViewPrefix || 'default';
      return `wave-chart-${prefix}-slot-${slotIndex}`;
    },

    /**
     * 判断预约项是否在当前展示页（displayedAppointments 与 appointmentData 为同一对象引用）
     */
    isAppointmentOnCurrentDisplayPage(item) {
      if (!item) return false;
      const patientKey = this.getPatientBindingKey(item);
      return (this.patientSlotPool || []).some((slot) => {
        if (!slot.visible || !slot.item) return false;
        if (slot.item === item) return true;
        return this.getPatientBindingKey(slot.item) === patientKey;
      });
    },

    /** 当前可见槽位的 slotKey 集合 */
    collectActiveSlotKeys() {
      const keys = new Set();
      (this.patientSlotPool || []).forEach((slot) => {
        if (slot.visible) {
          keys.add(this.getChartSlotKey(slot.slotIndex));
        }
      });
      return keys;
    },

    /**
     * 释放单个 roomKey 对应 ECharts 实例
     * @param {string} roomKey
     * @param {string} reason 生命周期原因：off-page | orphan-dom | dom-mismatch | dispose-all
     */
    disposeChartInstanceByKey(roomKey, reason = 'off-page') {
      const pool = this.getChartPool();
      const chart = pool.instances[roomKey];
      if (chart) {
        chartLifecycleStats.recordDispose(reason, roomKey);
        try {
          chart.dispose();
        } catch (e) {
          // 忽略
        }
      }
      delete pool.instances[roomKey];
      delete pool.optionCache[roomKey];
      if (pool.slotBinding) {
        delete pool.slotBinding[roomKey];
      }
    },

    /** 控制台打印图表生命周期汇总（含 appointmentData / 当前实例数） */
    printChartLifecycleSummary() {
      const pool = this.getChartPool();
      return chartLifecycleStats.summary({
        appointmentData: this.$parent?.appointmentData,
        activeChartInstances: Object.keys(pool.instances || {}).length,
      });
    },

    /**
     * 第三阶段：槽位池模式下轮播不 dispose 图表；waveData 由 pruneStaleWaveDataInAppointments 管理
     */
    disposeOffPageEndedCharts() {
      // 元素池：图表实例与 DOM 槽位绑定，切页仅 setOption，不在此 dispose
    },

    /** @deprecated 第三阶段槽位池不再按页 dispose */
    disposeChartsNotOnCurrentPage() {
      this.disposeOffPageEndedCharts();
    },

    /** 清空患者 waveData（已结束且超时的离屏患者） */
    clearWaveDataOnItem(item) {
      if (!item) return;
      this.$set(item, 'waveData', {
        delta: [],
        theta: [],
        alpha: [],
        beta: [],
        gamma: []
      });
    },

    /** 每条波线最多保留 maxPerBand 个点（离屏已结束患者的过渡压缩） */
    trimWaveDataOnItem(item, maxPerBand) {
      if (!item || !item.waveData) return;
      const limit = Math.max(10, maxPerBand || 50);
      WAVE_BANDS.forEach((type) => {
        const arr = item.waveData[type];
        if (Array.isArray(arr)) {
          trimWaveBandInPlace(arr, limit);
        }
      });
    },

    /**
     * 1.5 止血：回收 appointmentData 中离屏/已结束患者的 waveData，降低长时间运行内存。
     * - 已结束 + 不在当前页 + 超过 wavePruneAfterEndedMs → 清空 waveData
     * - 已结束 + 不在当前页 + 未超时 → 压缩为少量点
     */
    pruneStaleWaveDataInAppointments() {
      const parent = this.$parent;
      if (!parent || !Array.isArray(parent.appointmentData)) return;

      const performanceConfig = this.getPerformanceConfig();
      const pruneAfterMs = Number(performanceConfig.wavePruneAfterEndedMs) || 15 * 60 * 1000;
      const now = Date.now();

      parent.appointmentData.forEach((item) => {
        if (!item) return;
        const status = this.getTreatmentStatus(item);
        const onPage = this.isAppointmentOnCurrentDisplayPage(item);

        if (status === '已结束') {
          if (!item._treatmentEndedAt) {
            this.$set(item, '_treatmentEndedAt', now);
          }
          if (!onPage) {
            const endedAt = item._treatmentEndedAt || now;
            if (now - endedAt >= pruneAfterMs) {
              this.clearWaveDataOnItem(item);
            } else {
              this.trimWaveDataOnItem(item, 50);
            }
          }
        }
      });
    },

    /** 清理已脱离 DOM 的孤儿实例（Welcome/Default 切换或 v-for 离屏时 DOM 销毁），释放后 waveData 仍保留 */
    detachOrphanChartInstances() {
      Object.keys(this.getChartPool().instances).forEach((roomKey) => {
        const chart = this.getChartPool().instances[roomKey];
        if (!chart) return;
        const dom = chart.getDom && chart.getDom();
        if (!dom || !document.body.contains(dom)) {
          chartLifecycleStats.recordDispose('orphan-dom', roomKey);
          try {
            chart.dispose();
          } catch (e) {
            // 忽略
          }
          delete this.getChartPool().instances[roomKey];
          delete this.getChartPool().optionCache[roomKey];
          if (this.getChartPool().slotBinding) {
            delete this.getChartPool().slotBinding[roomKey];
          }
        }
      });
    },

    scheduleChartResize(chart) {
      const tid = setTimeout(() => {
        if (chart && !(chart.isDisposed && chart.isDisposed())) {
          chart.resize();
        }
      }, 100);
      this.chartResizeTimers.push(tid);
      if (this.chartResizeTimers.length > 20) {
        const stale = this.chartResizeTimers.splice(0, this.chartResizeTimers.length - 20);
        stale.forEach((id) => clearTimeout(id));
      }
    },

    ensureWaveDataOnItem(item) {
      if (!item) return;
      const treatmentStatus = this.getTreatmentStatus(item);
      if (treatmentStatus === '已结束') return;

      const isRealPatient = !item.isMock && item.roomId;
      const hasRealtimeArray = item.realtimeDataArray &&
        Array.isArray(item.realtimeDataArray) &&
        item.realtimeDataArray.length > 0;

      // 真实患者未收到脑波时：只保证空 waveData 壳，不从同 roomId 拷贝历史波线
      if (isRealPatient && !hasRealtimeArray) {
        if (!item.waveData) {
          this.$set(item, 'waveData', {
            delta: [], theta: [], alpha: [], beta: [], gamma: []
          });
        }
        return;
      }

      const hasWaveData = item.waveData && (
        (item.waveData.delta && item.waveData.delta.length > 0) ||
        (item.waveData.theta && item.waveData.theta.length > 0) ||
        (item.waveData.alpha && item.waveData.alpha.length > 0) ||
        (item.waveData.beta && item.waveData.beta.length > 0) ||
        (item.waveData.gamma && item.waveData.gamma.length > 0)
      );

      if (!hasWaveData && this.$parent && this.$parent.appointmentData) {
        const sourceItem = this.$parent.appointmentData.find(app =>
          app.roomId && item.roomId &&
          app.roomId.toString() === item.roomId.toString() &&
          app.waveData && (
            (app.waveData.delta && app.waveData.delta.length > 0) ||
            (app.waveData.theta && app.waveData.theta.length > 0) ||
            (app.waveData.alpha && app.waveData.alpha.length > 0) ||
            (app.waveData.beta && app.waveData.beta.length > 0) ||
            (app.waveData.gamma && app.waveData.gamma.length > 0)
          )
        );
        if (sourceItem && sourceItem.waveData) {
          this.$set(item, 'waveData', {
            delta: sourceItem.waveData.delta ? [...sourceItem.waveData.delta.map(d => ({ ...d }))] : [],
            theta: sourceItem.waveData.theta ? [...sourceItem.waveData.theta.map(d => ({ ...d }))] : [],
            alpha: sourceItem.waveData.alpha ? [...sourceItem.waveData.alpha.map(d => ({ ...d }))] : [],
            beta: sourceItem.waveData.beta ? [...sourceItem.waveData.beta.map(d => ({ ...d }))] : [],
            gamma: sourceItem.waveData.gamma ? [...sourceItem.waveData.gamma.map(d => ({ ...d }))] : []
          });
        }
      }

      if (!item.waveData) {
        this.$set(item, 'waveData', { delta: [], theta: [], alpha: [], beta: [], gamma: [] });
      } else {
        ['delta', 'theta', 'alpha', 'beta', 'gamma'].forEach((type) => {
          if (!item.waveData[type] || !Array.isArray(item.waveData[type])) {
            this.$set(item.waveData, type, []);
          }
        });
      }
    },

    /** 原地压缩 waveData 频段数组，不 spread 新建对象 */
    compressWaveData(data, targetLength) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        return [];
      }
      compressWaveBandInPlace(data, targetLength);
      return data;
    },
    preloadChartOptions(items) {
      if (!Array.isArray(items)) return;
      // 每次预加载前先清空，确保不会拿到上一页的配置
      this.preloadedChartOptions = {};
      this.preloadedPatientKeys = {};
      items.forEach((item, index) => {
        if (!item) return;
        const option = this.getChartOption(item);
        this.$set(this.preloadedChartOptions, index, option);
        this.$set(this.preloadedPatientKeys, index, this.getPatientBindingKey(item));
      });
    },
    /**
     * 轮播切页时清空当前可见槽位 Canvas，避免上一患者波线残留
     */
    clearVisibleSlotCharts() {
      (this.patientSlotPool || []).forEach((slot) => {
        if (slot.visible) {
          clearSlotChartInstance(this, slot.slotIndex);
        }
      });
    },
    /**
     * 取预加载 chart option（校验 patientKey 一致才使用）
     */
    takePreloadedChartOption(slotIndex, patientKey) {
      const pre = this.preloadedChartOptions && this.preloadedChartOptions[slotIndex];
      const key = this.preloadedPatientKeys && this.preloadedPatientKeys[slotIndex];
      if (pre && key && patientKey && key === patientKey) {
        this.$delete(this.preloadedChartOptions, slotIndex);
        this.$delete(this.preloadedPatientKeys, slotIndex);
        return pre;
      }
      if (pre !== undefined) {
        this.$delete(this.preloadedChartOptions, slotIndex);
      }
      if (key !== undefined) {
        this.$delete(this.preloadedPatientKeys, slotIndex);
      }
      return null;
    },
    /**
     * 是否应为该患者绘制脑波折线（与 updateAllCharts 判定一致）
     */
    shouldShowWaveformForItem(item) {
      if (!item) return false;
      if (this.getTreatmentStatus(item) === '已结束') return false;

      const waveData = item.waveData || {};
      const hasHistoryData = WAVE_BANDS.some(
        (band) => Array.isArray(waveData[band]) && waveData[band].length > 0
      );
      const isRealData = !item.isMock;
      const hasRealtimeDataArray = item.realtimeDataArray &&
        Array.isArray(item.realtimeDataArray) &&
        item.realtimeDataArray.length > 0;
      const hasValidLastValidData = item.lastValidBrainwaveData &&
        (item.lastValidBrainwaveData.delta > 0 ||
          item.lastValidBrainwaveData.theta > 0 ||
          item.lastValidBrainwaveData.alpha > 0 ||
          item.lastValidBrainwaveData.beta > 0 ||
          item.lastValidBrainwaveData.gamma > 0);

      const delta = item.delta;
      const theta = item.theta;
      const alpha = item.alpha;
      const beta = item.beta;
      const gamma = item.gamma;
      const hasMockBrainwaveValues = !isRealData && (
        (delta !== undefined && delta !== null && delta > 0) ||
        (theta !== undefined && theta !== null && theta > 0) ||
        (alpha !== undefined && alpha !== null && alpha > 0) ||
        (beta !== undefined && beta !== null && beta > 0) ||
        (gamma !== undefined && gamma !== null && gamma > 0)
      );

      return isRealData
        ? (hasRealtimeDataArray || hasValidLastValidData)
        : (hasHistoryData || hasValidLastValidData || hasMockBrainwaveValues);
    },
    /**
     * 换患者或强制清空后，写入槽位 chart option（无脑波源时用空 series）
     */
    applySlotChartOptionForItem(slotIndex, item, chart, patientKey, forceClear = false) {
      if (!chart || (chart.isDisposed && chart.isDisposed())) return;

      const slotKey = this.getChartSlotKey(slotIndex);
      const pool = this.getChartPool();

      if (forceClear || !item) {
        clearSlotChartInstance(this, slotIndex);
      }

      if (!item) {
        const emptyOption = this.getEmptyChartOption();
        chart.setOption(emptyOption, { notMerge: true, lazyUpdate: false, silent: true });
        pool.optionCache[slotKey] = emptyOption;
        return;
      }

      let option = this.getEmptyChartOption();
      if (this.shouldShowWaveformForItem(item)) {
        const pre = this.takePreloadedChartOption(slotIndex, patientKey);
        option = pre || this.getChartOption(item);
      }

      chart.setOption(option, { notMerge: true, lazyUpdate: false, silent: true });
      pool.optionCache[slotKey] = option;
    },
    updateSinglePatientBubble(patient) {
      if (!patient) return;
      const now = Date.now();
      if (!patient.bubbles) {
        this.$set(patient, 'bubbles', []);
      }
      if (!patient.lastBubbleTime) {
        this.$set(patient, 'lastBubbleTime', 0);
      }
      const treatmentStatus = this.getTreatmentStatus(patient);
      if (treatmentStatus === '已结束') {
        return;
      }
      const sleepStatus = this.getSleepStatus(patient);
      const bubbleValue = patient.lastAddedScore || (this.helpers.getSleepBubbleValue ? this.helpers.getSleepBubbleValue(sleepStatus) : 0);
      patient.lastAddedScore = undefined;
      if (bubbleValue >= 0) {
        patient.lastBubbleTime = now;
        const positions = [
          { top: '60px', left: '10px' },
          { top: '60px', right: '10px' },
          { top: '60px', left: '20px' },
          { top: '60px', right: '30px' },
          { top: '60px', left: '30px' },
          { top: '60px', right: '20px' }
        ];
        let availablePositions = [...positions];
        if (patient.bubbles.length > 0) {
          const existingPositions = patient.bubbles.map(b => JSON.stringify(b.position));
          availablePositions = positions.filter(pos => !existingPositions.includes(JSON.stringify(pos)));
        }
        const randomPosition = availablePositions.length > 0 
          ? availablePositions[Math.floor(Math.random() * availablePositions.length)]
          : positions[Math.floor(Math.random() * positions.length)];
        if (this.helpers.addSystemLog) {
          this.helpers.addSystemLog(`${patient.roomName} - ${patient.patientName} 产生${sleepStatus}气泡 +${bubbleValue}`);
        }
        const jitterDelay = Math.floor(Math.random() * 500);
        const newBubble = {
          id: `${now}-${Math.floor(Math.random() * 10000)}-${patient.roomName}-${patient.patientName}`,
          type: sleepStatus,
          value: bubbleValue,
          visible: true,
          opacity: 0,
          position: randomPosition
        };
        const t1 = setTimeout(() => {
          const bubbleIndex = patient.bubbles.length;
          this.$set(patient.bubbles, bubbleIndex, newBubble);
          screenLifecycleStats.recordBubbleCreated({
            roomName: patient.roomName,
            patientName: patient.patientName,
          });
          const t2 = setTimeout(() => {
            const index = patient.bubbles.findIndex(b => b.id === newBubble.id);
            if (index !== -1) {
              this.$set(patient.bubbles[index], 'opacity', 1);
            }
          }, 100);
          this.bubbleTimers.push(t2);
          const t3 = setTimeout(() => {
            const index = patient.bubbles.findIndex(b => b.id === newBubble.id);
            if (index !== -1) {
              this.$delete(patient.bubbles, index);
            }
          }, 4000);
          this.bubbleTimers.push(t3);
        }, jitterDelay);
        this.bubbleTimers.push(t1);
      }
    },
    updateBubbles() {
      if (this.isSliding) return;
      const now = Date.now();
      // 修复气泡问题：确保只处理当前页面的患者
      // displayedAppointments 应该只包含当前页面的患者，但为了安全起见，再次过滤
      const currentPageAppointments = (this.patientSlotPool || [])
        .filter((slot) => slot.visible && slot.item)
        .map((slot) => slot.item);
      if (currentPageAppointments && currentPageAppointments.length > 0) {
        const maxBubblesPerPage = Math.max(
          1,
          (this.patientSlotPool || []).filter((slot) => slot.visible).length || 4
        );
        const limitedAppointments = currentPageAppointments.slice(0, maxBubblesPerPage);

        limitedAppointments.forEach((item) => {
          const slot = (this.patientSlotPool || []).find((s) => s.item === item);
          const slotIndex = slot ? slot.slotIndex : 0;
          const containerId = this.getChartDomId(slotIndex);
          const container = document.getElementById(containerId);
          if (!container) {
            return;
          }
          if (!item.bubbles) {
            this.$set(item, 'bubbles', []);
          }
          // 清理过期气泡（超过 5 秒），防止内存泄漏和堆积
          if (item.bubbles && item.bubbles.length > 0) {
            const currentTime = Date.now();
            item.bubbles = item.bubbles.filter(bubble => {
              // 从气泡 ID 中提取时间戳（格式：timestamp-随机数-...）
              if (!bubble.id) return false;
              const idParts = bubble.id.split('-');
              const bubbleTime = parseInt(idParts[0]) || 0;
              // 保留 5 秒内的气泡（比 4 秒销毁时间稍长，确保已过销毁时间）
              return (currentTime - bubbleTime) < 5000;
            });
            
            // 限制数组最大长度为 10，防止异常情况下无限增长
            if (item.bubbles.length > 10) {
              item.bubbles = item.bubbles.slice(-10); // 保留最新的 10 个
            }
          }
          if (!item.lastBubbleTime) {
            this.$set(item, 'lastBubbleTime', 0);
          }
          const treatmentStatus = this.getTreatmentStatus(item);
          if (treatmentStatus === '已结束') {
            return;
          }
          // 限制每个患者同时最多显示 2 个气泡
          if (item.bubbles.length >= 2) {
            return;
          }
          // 增加生成间隔到 4 秒，避免气泡生成过于频繁
          if (now - item.lastBubbleTime < 4000 && item.bubbles.length > 0) {
            return;
          }
          const sleepStatus = this.getSleepStatus(item);
          const bubbleValue = item.lastAddedScore || (this.helpers.getSleepBubbleValue ? this.helpers.getSleepBubbleValue(sleepStatus) : 0);
          item.lastAddedScore = undefined;
          if (bubbleValue >= 0) {
            item.lastBubbleTime = now;
            const positions = [
              { top: '60px', left: '10px' },
              { top: '60px', right: '10px' },
              { top: '60px', left: '20px' },
              { top: '60px', right: '30px' },
              { top: '60px', left: '30px' },
              { top: '60px', right: '20px' }
            ];
            let availablePositions = [...positions];
            if (item.bubbles.length > 0) {
              const existingPositions = item.bubbles.map(b => JSON.stringify(b.position));
              availablePositions = positions.filter(pos => !existingPositions.includes(JSON.stringify(pos)));
            }
            const randomPosition = availablePositions.length > 0 
              ? availablePositions[Math.floor(Math.random() * availablePositions.length)]
              : positions[Math.floor(Math.random() * positions.length)];
            if (this.helpers.addSystemLog) {
              this.helpers.addSystemLog(`${item.roomName} - ${item.patientName} 产生${sleepStatus}气泡 +${bubbleValue}`);
            }
            const jitterDelay = Math.floor(Math.random() * 500);
            const newBubble = {
              id: `${now}-${Math.floor(Math.random() * 10000)}-${item.roomName}-${item.patientName}`,
              type: sleepStatus,
              value: bubbleValue,
              visible: true,
              opacity: 0,
              position: randomPosition
            };
            const t1 = setTimeout(() => {
              const bubbleIndex = item.bubbles.length;
              this.$set(item.bubbles, bubbleIndex, newBubble);
              screenLifecycleStats.recordBubbleCreated({
                roomName: item.roomName,
                patientName: item.patientName,
              });
              const t2 = setTimeout(() => {
                const index = item.bubbles.findIndex(b => b.id === newBubble.id);
                if (index !== -1) {
                  this.$set(item.bubbles[index], 'opacity', 1);
                }
              }, 100);
              this.bubbleTimers.push(t2);
              const t3 = setTimeout(() => {
                const index = item.bubbles.findIndex(b => b.id === newBubble.id);
                if (index !== -1) {
                  this.$delete(item.bubbles, index);
                }
              }, 4000);
              this.bubbleTimers.push(t3);
            }, jitterDelay);
            this.bubbleTimers.push(t1);
          }
        });
      }
    },
    /**
     * 初始化槽位图表（单入口：mounted / 轮播 finish / activated）
     * 第三阶段：固定 8 槽 DOM，按 slotIndex 复用 ECharts，换患者仅 setOption
     */
    initCharts(onComplete) {
      chartLifecycleStats.recordInitChartsCall();
      this.$nextTick(() => {
        this.lastChartUpdateTime = 0;
        this.pruneStaleWaveDataInAppointments();
        this.detachOrphanChartInstances();

        (this.patientSlotPool || []).forEach((slot) => {
          this.initSlotChart(slot.slotIndex, slot.item, slot.visible);
        });

        if (typeof onComplete === 'function') {
          this.$nextTick(onComplete);
        }
      });
    },

    /**
     * 初始化或复用单个槽位图表
     * @param {number} slotIndex 槽位 0..7
     * @param {object|null} item 当前绑定患者
     * @param {boolean} visible 是否当前页可见
     */
    initSlotChart(slotIndex, item, visible = true) {
      const slotKey = this.getChartSlotKey(slotIndex);
      const chartDomId = this.getChartDomId(slotIndex);
      const chartDom = document.getElementById(chartDomId);
      if (!chartDom) return;

      // 隐藏槽位（如全屏模式下 slot 4~7）不 init，避免 0 宽高警告与多余实例
      if (!visible) {
        return;
      }

      const pool = this.getChartPool();
      const patientKey = item ? this.getPatientBindingKey(item) : null;
      const patientChanged = pool.slotBinding[slotKey] !== patientKey;

      if (item) {
        this.ensureWaveDataOnItem(item);
      }

      // 容器尚未布局完成时延后，由 rAF/短延迟重试，不走 updateChart-fallback
      if (!this.isChartDomReady(chartDom)) {
        if (item) {
          this.scheduleSlotChartInitWhenReady(slotIndex, item);
        }
        return;
      }

      let chart = pool.instances[slotKey];
      if (chart && !(chart.isDisposed && chart.isDisposed())) {
        const boundDom = chart.getDom && chart.getDom();
        if (boundDom === chartDom) {
          chartLifecycleStats.recordReuse(slotKey, {
            roomId: item?.roomId,
            domId: chartDomId,
            patientChanged,
          });
          pool.slotBinding[slotKey] = patientKey;

          if (patientChanged) {
            this.applySlotChartOptionForItem(slotIndex, item, chart, patientKey, true);
          } else if (!item) {
            this.applySlotChartOptionForItem(slotIndex, null, chart, patientKey, true);
          } else {
            this.updateChart(slotIndex, item);
          }
          return;
        }
        chartLifecycleStats.recordDispose('dom-mismatch', slotKey);
        try {
          chart.dispose();
        } catch (e) {
          // 忽略
        }
        delete pool.instances[slotKey];
        chart = null;
      }

      const existingOnDom = echarts.getInstanceByDom(chartDom);
      if (existingOnDom) {
        chartLifecycleStats.recordBindExistingDom(slotKey, {
          roomId: item?.roomId,
          domId: chartDomId,
        });
        pool.instances[slotKey] = existingOnDom;
        pool.slotBinding[slotKey] = patientKey;
        this.applySlotChartOptionForItem(slotIndex, item, existingOnDom, patientKey, patientChanged);
        this.scheduleChartResize(existingOnDom);
        return;
      }

      chart = echarts.init(chartDom);
      chartLifecycleStats.recordEchartsInit('initCharts-new', slotKey, {
        roomId: item?.roomId,
        domId: chartDomId,
        patientName: item?.patientName,
        slotIndex,
      });
      pool.instances[slotKey] = chart;
      pool.slotBinding[slotKey] = patientKey;
      this.applySlotChartOptionForItem(slotIndex, item, chart, patientKey, true);
      this.scheduleChartResize(chart);
    },
    initChart(slotIndex, item) {
      this.initSlotChart(slotIndex, item, true);
    },
    /**
     * 兼容旧调用：仅清理脱离 DOM 的孤儿实例，不再按 epoch/页码销毁当前有效实例
     */
    cleanupOffPageCharts() {
      this.detachOrphanChartInstances();
    },
    getEmptyChartOption() {
      return {
        backgroundColor: 'transparent',
        grid: { top: 10, right: 10, bottom: 10, left: 10, containLabel: false },
        xAxis: { type: 'category', data: [], show: false },
        yAxis: { type: 'value', show: false },
        series: [],
        animation: false
      };
    },
    /** 清空指定槽位图表，避免轮播残留上一患者波线 */
    clearSlotChart(slotIndex) {
      clearSlotChartInstance(this, slotIndex);
    },
    /** 完整 ECharts 配置（首次 init 使用） */
    getChartOption(item) {
      if (this.getTreatmentStatus(item) === '已结束') {
        return this.getEmptyChartOption();
      }
      const payload = this.buildChartSeriesPayload(item);
      if (!payload) {
        return this.getEmptyChartOption();
      }
      return {
        backgroundColor: 'transparent',
        grid: { top: 10, right: 10, bottom: 10, left: 10, containLabel: false },
        xAxis: { type: 'category', data: payload.xAxisData, show: false },
        yAxis: { type: 'value', show: false },
        series: payload.series,
        animation: false
      };
    },
    /**
     * 仅构建动态数据（series + xAxisData），供定时 updateChart 增量更新。
     * 校验不通过时返回 null，避免每 tick 创建完整 option 对象。
     */
    getChartSeriesPayload(item) {
      const payload = this.buildChartSeriesPayload(item);
      return payload || { series: [], xAxisData: [] };
    },
    buildChartSeriesPayload(item) {
      if (this.getTreatmentStatus(item) === '已结束') {
        return null;
      }
      
      const waveData = item.waveData || { delta: [], theta: [], alpha: [], beta: [], gamma: [] };
      
      // 关键修复：检查当前脑波值，如果为 undefined，尝试从 lastValidBrainwaveData 恢复
      // 避免将 undefined 错误转换为 0
      let delta = item.delta;
      let theta = item.theta;
      let alpha = item.alpha;
      let beta = item.beta;
      let gamma = item.gamma;
      
      // 如果值为 undefined，尝试从 lastValidBrainwaveData 恢复
      if (item.lastValidBrainwaveData) {
        if (delta === undefined || delta === null) delta = item.lastValidBrainwaveData.delta;
        if (theta === undefined || theta === null) theta = item.lastValidBrainwaveData.theta;
        if (alpha === undefined || alpha === null) alpha = item.lastValidBrainwaveData.alpha;
        if (beta === undefined || beta === null) beta = item.lastValidBrainwaveData.beta;
        if (gamma === undefined || gamma === null) gamma = item.lastValidBrainwaveData.gamma;
      }
      
      // 关键修复：只有在所有值都明确为 0 时才判断为全0（不包括 undefined）
      // 如果值为 undefined，说明数据可能丢失，不应该判断为全0
      const currentAllZero = (delta === 0 || delta === undefined) && 
                            (theta === 0 || theta === undefined) && 
                            (alpha === 0 || alpha === undefined) && 
                            (beta === 0 || beta === undefined) && 
                            (gamma === 0 || gamma === undefined) &&
                            delta !== undefined && theta !== undefined && alpha !== undefined && beta !== undefined && gamma !== undefined;
      
      // 检查历史数据是否存在
      const hasHistoryData = (waveData.delta && waveData.delta.length > 0) ||
                            (waveData.theta && waveData.theta.length > 0) ||
                            (waveData.alpha && waveData.alpha.length > 0) ||
                            (waveData.beta && waveData.beta.length > 0) ||
                            (waveData.gamma && waveData.gamma.length > 0);
      
      // 关键修复：检查是否有真实的脑波数据源
      // 优先级：1. 有历史数据（waveData）优先，2. 有实时数据数组，3. 有最后有效数据
      // 排除虚拟数据（isMock）
      const isRealData = !item.isMock;
      
      // 1. 检查是否有 realtimeDataArray（表示接收过实时数据）
      const hasRealtimeDataArray = item.realtimeDataArray && Array.isArray(item.realtimeDataArray) && item.realtimeDataArray.length > 0;
      
      // 2. 关键修复：对于虚拟数据和真实数据，都优先使用lastValidBrainwaveData判断
      // 对于真实数据，如果脑波值为0/undefined但存在lastValidBrainwaveData（可能是生成的虚拟数据），也应该认为有数据源
      let hasValidLastValidData = false;
      if (item.lastValidBrainwaveData) {
        // 无论是虚拟数据还是真实数据，都检查lastValidBrainwaveData
        hasValidLastValidData = (item.lastValidBrainwaveData.delta > 0 || 
                                 item.lastValidBrainwaveData.theta > 0 ||
                                 item.lastValidBrainwaveData.alpha > 0 ||
                                 item.lastValidBrainwaveData.beta > 0 ||
                                 item.lastValidBrainwaveData.gamma > 0);
      }
      
      // 2.5. 检查模拟数据是否有脑波值（模拟数据只要有脑波值就显示，不需要其他条件）
      // 关键修复：对于虚拟数据，如果lastValidBrainwaveData存在且有效，也应该认为有脑波值
      const hasMockBrainwaveValues = !isRealData && (
        hasValidLastValidData || // 优先使用lastValidBrainwaveData
        (delta !== undefined && delta !== null && delta > 0) ||
        (theta !== undefined && theta !== null && theta > 0) ||
        (alpha !== undefined && alpha !== null && alpha > 0) ||
        (beta !== undefined && beta !== null && beta > 0) ||
        (gamma !== undefined && gamma !== null && gamma > 0)
      );
      
      // 真实数据：存在有效 realtimeDataArray 或有效缓存时才展示波线
      const hasRealBrainwaveDataSource = isRealData
        ? (hasRealtimeDataArray || hasValidLastValidData)
        : (hasHistoryData || hasValidLastValidData || hasMockBrainwaveValues);
      
      const treatmentStatus = this.getTreatmentStatus(item);
      const isTreatmentEnded = treatmentStatus === '已结束';
      
      // 关键修复：只有在有真实数据源时才显示波线
      // 如果没有真实数据源，即使治疗中也返回空配置
      if (!hasRealBrainwaveDataSource) {
        logger.debug(`getChartOption: 返回空配置 - 没有真实的脑波数据源`, {
          roomId: item.roomId,
          isMock: item.isMock,
          hasRealtimeDataArray,
          hasValidLastValidData,
          hasHistoryData,
          currentValues: { delta, theta, alpha, beta, gamma },
          治疗状态: treatmentStatus
        });
        return null;
      }
      
      // 如果治疗已结束且无历史数据，返回空配置
      // 对于虚拟数据，即使治疗结束，只要有历史数据或脑波值就显示
      const shouldShowForMock = !isRealData && (hasHistoryData || hasMockBrainwaveValues);
      if (isTreatmentEnded && !hasHistoryData && currentAllZero && !shouldShowForMock) {
        // 调试日志：记录为什么返回空配置
        logger.debug(`getChartOption: 返回空配置 - 治疗已结束且无历史数据`, {
          roomId: item.roomId,
          isMock: item.isMock,
          currentValues: { delta, theta, alpha, beta, gamma },
          waveDataLengths: {
            delta: waveData.delta?.length || 0,
            theta: waveData.theta?.length || 0,
            alpha: waveData.alpha?.length || 0,
            beta: waveData.beta?.length || 0,
            gamma: waveData.gamma?.length || 0
          },
          治疗状态: treatmentStatus,
          shouldShowForMock
        });
        return null;
      }
      
      // 关键修复：如果有真实数据源，即使当前值为0，也要显示波线
      // 这是用户的核心需求：波线一直展示，无论轮播几次，直到治疗结束
      if (hasRealBrainwaveDataSource) {
        //   治疗状态: treatmentStatus
        // 继续执行，显示波线
      }
      
      // 调试日志：记录数据读取情况
      if (hasHistoryData) {
        //   currentAllZero
      }
      // 计算最大数据长度，确保能显示所有历史数据
      const maxDataLength = Math.max(
        waveData.delta?.length || 0,
        waveData.theta?.length || 0,
        waveData.alpha?.length || 0,
        waveData.beta?.length || 0,
        waveData.gamma?.length || 0,
        50
      );
      
      // 优化容器宽度：使用实际容器宽度（400px），增加可显示的数据点数
      const containerWidth = 400; // 从CSS中看到实际宽度是400px
      const performanceConfig = this.getPerformanceConfig();
      const configuredDisplayPoints = Number(performanceConfig.waveDisplayPoints);
      const maxDisplayPoints = configuredDisplayPoints > 0
        ? configuredDisplayPoints
        : Math.floor(containerWidth / 1.2);
      
      // 优化后的数据压缩函数：智能采样，保留关键特征点（最大值、最小值、起始点、结束点）
      const compressData = (data, targetLength) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
          return [];
        }
        
        // 如果数据量小于等于目标长度，返回所有数据
        if (data.length <= targetLength) {
          return data.map(point => {
            if (typeof point === 'object' && point !== null) {
              return point.value !== undefined ? point.value : point;
            }
            return point;
          });
        }
        
        // 提取数值
        const values = data.map(point => {
          if (typeof point === 'object' && point !== null) {
            return point.value !== undefined ? point.value : point;
          }
          return point;
        });
        
        // 智能压缩算法：
        // 1. 保留起始点和结束点
        // 2. 保留最大值和最小值点
        // 3. 在剩余空间中均匀采样
        const result = [];
        const indices = new Set(); // 使用Set避免重复索引
        
        // 1. 保留起始点
        indices.add(0);
        // 2. 保留结束点
        indices.add(data.length - 1);
        
        // 3. 找到最大值和最小值的索引
        let maxIndex = 0;
        let minIndex = 0;
        let maxValue = values[0];
        let minValue = values[0];
        
        for (let i = 1; i < values.length; i++) {
          if (values[i] > maxValue) {
            maxValue = values[i];
            maxIndex = i;
          }
          if (values[i] < minValue) {
            minValue = values[i];
            minIndex = i;
          }
        }
        
        // 只有当最大值和最小值点与起始/结束点不同时才添加
        if (maxIndex !== 0 && maxIndex !== data.length - 1) {
          indices.add(maxIndex);
        }
        if (minIndex !== 0 && minIndex !== data.length - 1 && minIndex !== maxIndex) {
          indices.add(minIndex);
        }
        
        // 4. 计算剩余需要的点数
        const remainingPoints = targetLength - indices.size;
        if (remainingPoints > 0) {
          // 5. 均匀采样剩余的点（排除已经选择的关键点）
          const step = (data.length - 1) / (remainingPoints + 1);
          for (let i = 0; i < remainingPoints; i++) {
            let candidateIndex = Math.round((i + 1) * step);
            
            // 如果候选索引已经被选择，尝试前后偏移
            if (indices.has(candidateIndex)) {
              // 尝试向后查找
              let offset = 1;
              while (candidateIndex + offset < data.length && indices.has(candidateIndex + offset)) {
                offset++;
              }
              if (candidateIndex + offset < data.length) {
                candidateIndex = candidateIndex + offset;
              } else {
                // 如果向后不行，尝试向前
                offset = -1;
                while (candidateIndex + offset >= 0 && indices.has(candidateIndex + offset)) {
                  offset--;
                }
                if (candidateIndex + offset >= 0) {
                  candidateIndex = candidateIndex + offset;
                }
              }
            }
            
            indices.add(candidateIndex);
          }
        }
        
        // 6. 将索引排序并提取对应的值
        const sortedIndices = Array.from(indices).sort((a, b) => a - b);
        // 如果排序后的索引数量超过目标长度，保留前targetLength个
        const finalIndices = sortedIndices.slice(0, targetLength);
        
        // 确保起始点和结束点被包含
        if (finalIndices[0] !== 0) {
          finalIndices[0] = 0;
        }
        if (finalIndices[finalIndices.length - 1] !== data.length - 1) {
          finalIndices[finalIndices.length - 1] = data.length - 1;
        }
        
        // 去重并排序
        const uniqueIndices = [...new Set(finalIndices)].sort((a, b) => a - b);
        
        return uniqueIndices.map(index => values[index]);
      };
      
      // 获取显示数据：使用压缩算法确保所有历史数据都能显示
      const getLatestData = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
          return [];
        }
        
        // 如果数据量小于等于显示点数，返回所有数据
        if (data.length <= maxDisplayPoints) {
          return data.map(point => {
            if (typeof point === 'object' && point !== null) {
              return point.value !== undefined ? point.value : point;
            }
            return point;
          });
        }
        
        // 如果数据量大于显示点数，使用压缩算法
        // 关键修复：压缩所有历史数据，而不是只取最新的数据点
        return compressData(data, maxDisplayPoints);
      };
      
      // 计算实际显示长度（压缩后的数据长度）
      const displayLength = Math.min(maxDataLength, maxDisplayPoints);
      const xAxisData = Array.from({ length: displayLength }, (_, i) => i);
      
      // 构建series数组，只包含有数据的波线
      const series = [];
      // 修复波线过于平整问题：降低smooth程度，从true改为0.3，让波线有更多起伏
      if (waveData.delta && waveData.delta.length > 0) {
        series.push({ name: 'Delta', type: 'line', data: getLatestData(waveData.delta), smooth: 0.3, symbol: 'none', lineStyle: { color: '#FF5722', width: 1 } });
      }
      if (waveData.theta && waveData.theta.length > 0) {
        series.push({ name: 'Theta', type: 'line', data: getLatestData(waveData.theta), smooth: 0.3, symbol: 'none', lineStyle: { color: '#FFEB3B', width: 1 } });
      }
      if (waveData.alpha && waveData.alpha.length > 0) {
        series.push({ name: 'Alpha', type: 'line', data: getLatestData(waveData.alpha), smooth: 0.3, symbol: 'none', lineStyle: { color: '#4CAF50', width: 1 } });
      }
      if (waveData.beta && waveData.beta.length > 0) {
        series.push({ name: 'Beta', type: 'line', data: getLatestData(waveData.beta), smooth: 0.3, symbol: 'none', lineStyle: { color: '#2196F3', width: 1 } });
      }
      if (waveData.gamma && waveData.gamma.length > 0) {
        series.push({ name: 'Gamma', type: 'line', data: getLatestData(waveData.gamma), smooth: 0.3, symbol: 'none', lineStyle: { color: '#9C27B0', width: 1 } });
      }

      return { series, xAxisData };
    },
    updateAllCharts() {
      if (this.isSliding || this.$parent?.isRotating) return;

      // 定时刷新前轻量 prune（init 时也会执行，双保险）
      this.pruneStaleWaveDataInAppointments();

      (this.patientSlotPool || []).forEach((slot) => {
        if (!slot.visible || !slot.item) return;
        const item = slot.item;
        const slotIndex = slot.slotIndex;
        
        // 检查当前脑波值是否都为0
        const delta = item.delta || 0;
        const theta = item.theta || 0;
        const alpha = item.alpha || 0;
        const beta = item.beta || 0;
        const gamma = item.gamma || 0;
        const currentAllZero = delta === 0 && theta === 0 && alpha === 0 && beta === 0 && gamma === 0;
        
        // 检查历史数据是否存在
        const waveData = item.waveData || { delta: [], theta: [], alpha: [], beta: [], gamma: [] };
        const hasHistoryData = (waveData.delta && waveData.delta.length > 0) ||
                              (waveData.theta && waveData.theta.length > 0) ||
                              (waveData.alpha && waveData.alpha.length > 0) ||
                              (waveData.beta && waveData.beta.length > 0) ||
                              (waveData.gamma && waveData.gamma.length > 0);
        
        // 关键修复：检查是否有真实的脑波数据源
        // 优先级：1. 有历史数据（waveData）优先，2. 有实时数据数组，3. 有最后有效数据
        // 排除虚拟数据（isMock）
        const isRealData = !item.isMock;
        
        // 1. 检查是否有 realtimeDataArray（表示接收过实时数据）
        const hasRealtimeDataArray = item.realtimeDataArray && Array.isArray(item.realtimeDataArray) && item.realtimeDataArray.length > 0;
        
        // 2. 检查是否有 lastValidBrainwaveData 且有有效值
        const hasValidLastValidData = item.lastValidBrainwaveData && 
                                     (item.lastValidBrainwaveData.delta > 0 || 
                                      item.lastValidBrainwaveData.theta > 0 ||
                                      item.lastValidBrainwaveData.alpha > 0 ||
                                      item.lastValidBrainwaveData.beta > 0 ||
                                      item.lastValidBrainwaveData.gamma > 0);
        
        // 2.5. 检查模拟数据是否有脑波值（模拟数据只要有脑波值就显示，不需要其他条件）
        const hasMockBrainwaveValues = !isRealData && (
          (delta !== undefined && delta !== null && delta > 0) ||
          (theta !== undefined && theta !== null && theta > 0) ||
          (alpha !== undefined && alpha !== null && alpha > 0) ||
          (beta !== undefined && beta !== null && beta > 0) ||
          (gamma !== undefined && gamma !== null && gamma > 0)
        );
        
        // 真实数据：存在有效 realtimeDataArray 或有效缓存时才展示波线
        const hasRealBrainwaveDataSource = isRealData
          ? (hasRealtimeDataArray || hasValidLastValidData)
          : (hasHistoryData || hasValidLastValidData || hasMockBrainwaveValues);
        
        // 检查治疗状态
        const treatmentStatus = this.getTreatmentStatus(item);
        const isTreatmentEnded = treatmentStatus === '已结束';
        
        // 关键修复：只有在有真实数据源时才显示波线
        const shouldShowWaveform = hasRealBrainwaveDataSource;
        this.$set(item, 'hideWaveform', !shouldShowWaveform);
        
        // 如果不需要显示脑波图，只隐藏图表，但不清空历史数据
        // 关键修改：不清空 waveData，保留历史数据，以便轮播回来后能继续显示
        if (!shouldShowWaveform) {
          clearSlotChartInstance(this, slotIndex);
          return;
        }
        
        // 立即初始化 waveData：如果有脑波值但 waveData 为空，立即添加初始数据点，确保第一次就能显示波线
        if (shouldShowWaveform) {
          if (!item.waveData) {
            item.waveData = { delta: [], theta: [], alpha: [], beta: [], gamma: [] };
          }
          // 关键修复：检查是否有脑波值但 waveData 数组为空，如果是则立即初始化
          // 但不要将 undefined 转换为 0，应该从 lastValidBrainwaveData 恢复
          const hasBrainwaveValue = (delta !== undefined && delta !== 0) || 
                                   (theta !== undefined && theta !== 0) || 
                                   (alpha !== undefined && alpha !== 0) || 
                                   (beta !== undefined && beta !== 0) || 
                                   (gamma !== undefined && gamma !== 0);
          const waveDataEmpty = !item.waveData.delta || item.waveData.delta.length === 0;
          // 1.5：真实数据 waveData 仅由 useRealtimeData/updateWaveData 写入，此处不对真实数据 seed/push
          if (hasBrainwaveValue && waveDataEmpty && !isRealData) {
            const timestamp = Date.now();
            ['delta', 'theta', 'alpha', 'beta', 'gamma'].forEach(type => {
              let value = item[type];
              // 如果值为 undefined 或 null，尝试从 lastValidBrainwaveData 恢复
              if ((value === undefined || value === null) && item.lastValidBrainwaveData) {
                value = item.lastValidBrainwaveData[type];
              }
              // 只有在值有效（不是 undefined 和 null）时才添加数据点
              if (value !== undefined && value !== null) {
                if (!item.waveData[type]) {
                  item.waveData[type] = [];
                }
                const dataArray = item.waveData[type];
                // 限制单条波形历史长度，防止无限增长
                const performanceConfig = this.getPerformanceConfig();
                const maxWaveDataLength = Number(performanceConfig.waveHistoryLimit) || 1000;
                if (dataArray.length >= maxWaveDataLength) {
                  dataArray.splice(0, dataArray.length - (maxWaveDataLength - 1));
                }
                dataArray.push(createWavePoint(value, timestamp));
              }
            });
          }
        }
        // 虚拟/真实 waveData 均由 updateVirtualBrainwaveData / updateWaveData 单入口写入；此处仅刷新图表

        const chartDomId = this.getChartDomId(slotIndex);
        const chartDom = document.getElementById(chartDomId);
        if (chartDom) {
          this.updateChart(slotIndex, item);
        }
      });
    },
    resizeAllCharts() {
      Object.values(this.getChartPool().instances).forEach((chart) => {
        if (chart && chart.resize) {
          this.scheduleChartResize(chart);
        }
      });
    },
    disposeAllCharts() {
      // 先清理所有实例
      Object.keys(this.getChartPool().instances).forEach(id => {
        const chart = this.getChartPool().instances[id];
        if (chart) {
          chartLifecycleStats.recordDispose('dispose-all', id);
          try {
            // 先清除图表内容
            chart.clear();
            // 然后销毁实例
            if (chart.dispose) {
              chart.dispose();
            }
          } catch (e) {
            // 忽略已销毁的实例错误
          }
        }
      });
      this.getChartPool().instances = {};
      this.getChartPool().optionCache = {};
      if (this.getChartPool().slotBinding) {
        this.getChartPool().slotBinding = {};
      }
      
      // 强制清理所有可能的Canvas元素，包括不在实例管理中的
      const allChartContainers = document.querySelectorAll('[id^="wave-chart-"]');
      allChartContainers.forEach(container => {
        // 清理所有Canvas元素
        const canvases = container.querySelectorAll('canvas');
        canvases.forEach(canvas => {
          try {
            // 先停止所有可能的动画
            if (canvas.getContext) {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              }
            }
            canvas.remove();
          } catch (e) {
            // 忽略错误
          }
        });
        // 清空容器内容
        container.innerHTML = '';
        // 强制清理所有子节点
        while (container.firstChild) {
          try {
            container.removeChild(container.firstChild);
          } catch (e) {
            // 忽略错误
          }
        }
      });
    },
    /**
     * 增量更新单个槽位图表：仅 setOption series/xAxis，不重复 echarts.init
     */
    updateChart(slotIndex, item) {
      if (this.isSliding || this.$parent?.isRotating) return;
      if (!item) return;

      const slotKey = this.getChartSlotKey(slotIndex);
      const chartDomId = this.getChartDomId(slotIndex);
      const chartDom = document.getElementById(chartDomId);
      if (!chartDom) return;

      let chart = this.getChartPool().instances[slotKey];
      if (!chart || (chart.isDisposed && chart.isDisposed())) {
        chart = this.ensureSlotChartInstance(slotIndex, item);
        if (!chart) {
          // 实例尚未就绪（DOM 0 宽高或隐藏槽位），本 tick 跳过，等待 initCharts / scheduleSlotChartInitWhenReady
          return;
        }
      }

      const treatmentStatus = this.getTreatmentStatus(item);
      const isTreatmentEnded = treatmentStatus === '已结束';

      const waveData = item.waveData || { delta: [], theta: [], alpha: [], beta: [], gamma: [] };
      const hasHistoryData = (waveData.delta && waveData.delta.length > 0) ||
                            (waveData.theta && waveData.theta.length > 0) ||
                            (waveData.alpha && waveData.alpha.length > 0) ||
                            (waveData.beta && waveData.beta.length > 0) ||
                            (waveData.gamma && waveData.gamma.length > 0);

      const isRealData = !item.isMock;
      const hasRealtimeDataArray = item.realtimeDataArray && Array.isArray(item.realtimeDataArray) && item.realtimeDataArray.length > 0;
      const hasValidLastValidData = item.lastValidBrainwaveData &&
                                   (item.lastValidBrainwaveData.delta > 0 ||
                                    item.lastValidBrainwaveData.theta > 0 ||
                                    item.lastValidBrainwaveData.alpha > 0 ||
                                    item.lastValidBrainwaveData.beta > 0 ||
                                    item.lastValidBrainwaveData.gamma > 0);

      const delta = item.delta;
      const theta = item.theta;
      const alpha = item.alpha;
      const beta = item.beta;
      const gamma = item.gamma;
      const hasMockBrainwaveValues = !isRealData && (
        (delta !== undefined && delta !== null && delta > 0) ||
        (theta !== undefined && theta !== null && theta > 0) ||
        (alpha !== undefined && alpha !== null && alpha > 0) ||
        (beta !== undefined && beta !== null && beta > 0) ||
        (gamma !== undefined && gamma !== null && gamma > 0)
      );

      const hasRealBrainwaveDataSource = isRealData
        ? (hasRealtimeDataArray || hasValidLastValidData)
        : (hasHistoryData || hasValidLastValidData || hasMockBrainwaveValues);

      if (!hasRealBrainwaveDataSource || (isTreatmentEnded && !hasHistoryData)) {
        clearSlotChartInstance(this, slotIndex);
        return;
      }

      if (hasRealBrainwaveDataSource && item.hideWaveform) {
        this.$set(item, 'hideWaveform', false);
      }

      const payload = this.getChartSeriesPayload(item);
      if (!payload.series || payload.series.length === 0) {
        clearSlotChartInstance(this, slotIndex);
        return;
      }

      requestAnimationFrame(() => {
        const el = document.getElementById(chartDomId);
        if (!el) return;
        const inst = this.getChartPool().instances[slotKey];
        if (!inst || (inst.isDisposed && inst.isDisposed())) return;
        chartLifecycleStats.recordSetOption(slotKey);
        inst.setOption({
          xAxis: { data: payload.xAxisData },
          series: payload.series
        }, { notMerge: false, replaceMerge: ['series'], lazyUpdate: true, silent: true });
      });
    }
  }
};
